/**
 * useAutoTutorial — offers the brief welcome tour on the first 2 logins,
 * for BOTH customer types (replaces the old marketing-only auto-tutorial hook).
 * Shows a welcome prompt (WelcomeTourCard) instead of force-starting the tour.
 */
import { useCallback, useEffect, useState } from "react";
import { getUser } from "../helpers/utils";
import { useTutorial } from "./useTutorial";
import { TUTORIAL_STORAGE_KEYS } from "../components/Tutorial/types";
import {
  hasActivePromptOverlay,
  PROMPT_OVERLAY_EVENT,
} from "../pwa/prompt-overlay";

const SESSION_FLAG = "auto_tutorial_prompted_this_session";

export function getTutorialLoginCount(userId: string): number {
  const current = parseInt(
    localStorage.getItem(`tutorial_login_count_${userId}`) || "0",
    10
  );
  // Legacy key written for MarketingOnly users before the revamp
  const legacy = parseInt(
    localStorage.getItem(`marketing_tutorial_login_count_${userId}`) || "0",
    10
  );
  return Math.max(current, legacy);
}

export function useAutoTutorial(
  onboardingData: Record<string, unknown> | undefined
) {
  const { isActive } = useTutorial();
  const [showWelcomePrompt, setShowWelcomePrompt] = useState(false);
  // Re-run the offer check whenever a prompt scrim (e.g. the PWA install
  // coach) appears or clears, so the welcome card defers rather than stacking
  // its dialog over another full-screen prompt.
  const [overlayTick, setOverlayTick] = useState(0);
  useEffect(() => {
    const bump = () => setOverlayTick((t) => t + 1);
    window.addEventListener(PROMPT_OVERLAY_EVENT, bump);
    return () => window.removeEventListener(PROMPT_OVERLAY_EVENT, bump);
  }, []);

  useEffect(() => {
    const user = getUser();
    if (!user?.id) return;
    if (!onboardingData) return;
    if (onboardingData.isOnboardingCompleted !== true) return;

    // v2 marketing customers wait until their ads are live
    const userMetadata = onboardingData.userMetadata as
      | Record<string, unknown>
      | undefined;
    const isV2Marketing =
      user.type === "MarketingOnly" && userMetadata?.onboardingVersion === "v2";
    if (isV2Marketing && onboardingData.dashboardUnlocked !== true) return;

    if (isActive) return;
    // The install coach (and any other registered scrim) has priority — wait
    // for it to clear; PROMPT_OVERLAY_EVENT re-runs this effect when it does.
    if (hasActivePromptOverlay()) return;
    if (localStorage.getItem(TUTORIAL_STORAGE_KEYS.briefCompleted) === "true") return;
    if (localStorage.getItem(TUTORIAL_STORAGE_KEYS.welcomeDismissed) === "true") return;
    if (sessionStorage.getItem(SESSION_FLAG) === "true") return;

    const timeout = setTimeout(() => {
      if (hasActivePromptOverlay()) return;
      // Read the count inside the delayed callback so it always sees the
      // value written by main.tsx's per-session increment effect, regardless
      // of mount-time effect ordering.
      const loginCount = getTutorialLoginCount(user.id);
      if (loginCount < 1 || loginCount > 2) return;
      sessionStorage.setItem(SESSION_FLAG, "true");
      setShowWelcomePrompt(true);
    }, 1500);
    return () => clearTimeout(timeout);
  }, [isActive, onboardingData, overlayTick]);

  const closeWelcomePrompt = useCallback(() => setShowWelcomePrompt(false), []);
  const dismissForever = useCallback(() => {
    localStorage.setItem(TUTORIAL_STORAGE_KEYS.welcomeDismissed, "true");
    setShowWelcomePrompt(false);
  }, []);

  return { showWelcomePrompt, closeWelcomePrompt, dismissForever };
}
